// ---------- la célébration d'un palier ----------
// La carte qui annonce un palier gagné, et la pastille du trophée qui dit qu'il en attend un.
// Les règles de la file (une carte par badge, trois au plus, la carte d'accueil) sont dans
// src/badgeQueue.ts ; ici, le relevé « déjà vu », le réseau et le dessin.
//
// QUAND ELLE S'OUVRE. Jamais sur la carte de fin de partie : elle porte déjà le rejeu, les
// étoiles, le partage et la pub, et une feuille finie y gagne souvent trois paliers d'un coup. La
// victoire allume seulement la pastille du trophée ; la carte s'ouvre quand le joueur vient voir,
// par-dessus l'écran « Moi ».
//
// LE GESTE. La médaille SE COLORIE case par case avant de prendre son métal — le geste du jeu,
// pas des confettis génériques —, puis les neuf pigments du pointillisme en jaillissent.
//
// Aucune route n'a changé pour ça : tout passe par `GET /me/badges`, déjà là, et le relevé de ce
// qui a été montré reste sur l'appareil.

import { currentUser } from './account';
import { fetchBadges, type BadgeState, type BadgeTier, type BadgesReport } from './api';
import { noMotion } from './anim';
import {
  TIERS,
  buildQueue,
  fingerprint,
  firstLook,
  markSeen,
  rankOf,
  skippedTiers,
  type Card,
  type Seen,
} from './badgeQueue';
import { currentLang, t, type I18nKey } from './i18n';
import { buzz } from './platform';
import {
  badgeName,
  cachedBadges,
  isMeVisible,
  medal,
  onFreshBadges,
  progress,
  rememberBadges,
} from './profile';
import { PIGMENTS } from '../shared/alphabets/pointille';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

const SEEN_KEY = 'codigrid:badges:seen:v1';

/** Les métaux de la médaille qui se colorie : ombre, corps, clair, reflet. Les trois premiers
 *  sont ceux de .me-medal dans style.css (--m3, --m2, --m1), avec un ton intermédiaire en plus —
 *  à trois tons seulement, le modelé en aplats tournait à la cible. */
const METAL: Record<Exclude<BadgeTier, 'prisme'>, readonly string[]> = {
  bronze: ['#6e3e17', '#a9662f', '#c98447', '#f0b07a'],
  argent: ['#4d5e70', '#8d9fb2', '#b6c3d1', '#eef3f8'],
  or: ['#8a5d05', '#dba51a', '#f0c548', '#ffe58f'],
  platine: ['#1f6377', '#4fb3cf', '#86d3e6', '#d3f5fd'],
};

/** Le diamètre de la grande médaille, en px CSS — celui de .me-medal.xxl. */
const SIZE = 132;
/** Cases sur un diamètre : assez pour qu'on lise une grille, assez peu pour qu'on voie chaque
 *  case se poser. */
const GRID = 13;

// ---------- le relevé ----------

function readSeen(): Seen | null {
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) ?? 'null') as Seen | null;
    return raw && typeof raw.owner === 'string' && raw.ranks ? raw : null;
  } catch {
    return null;
  }
}

function writeSeen(s: Seen): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(s));
  } catch {
    /* sans stockage, les mêmes paliers se représenteront : un défaut de fête, pas une perte */
  }
}

// ---------- l'état ----------

let report: BadgesReport | null = null;
let seen: Seen | null = null;
let queue: Card[] = [];
let pos = 0;
let isOpen = false;
/** « Plus tard » vient d'être dit : plus d'ouverture d'office avant que le joueur ne rouvre le
 *  trophée. Sans ça, passer d'« Ensemble » à « Moi » refaisait une requête fraîche, et la carte
 *  qu'on venait de repousser revenait aussitôt. */
let snoozed = false;
/** Ce qui avait le focus avant l'ouverture, pour le lui rendre : sinon il reste dans un calque
 *  `aria-hidden`, ce que les lecteurs d'écran (et Chrome) signalent. */
let focusBack: HTMLElement | null = null;

function paintDot(): void {
  $('progressionDot').hidden = !queue.length;
}

/** Une réponse du serveur, fraîche ou en cache, et l'empreinte du compte à qui elle appartient :
 *  on en tire la file et la pastille. Une réponse d'un autre compte que le compte ACTUEL (code de
 *  reprise échangé pendant la requête) est ignorée — elle écrirait le relevé « déjà vu » du
 *  nouveau compte avec les badges de l'ancien. */
async function consider(r: BadgesReport, owner: string): Promise<void> {
  const token = await currentUser();
  if (!token || fingerprint(token) !== owner) return;
  report = r;
  seen = readSeen();
  if (!seen || seen.owner !== owner) {
    seen = firstLook(owner, r.badges);
    writeSeen(seen);
  }
  // Une carte déjà ouverte garde sa file jusqu'au bout : la refaire sous les doigts du joueur
  // changerait le compteur pendant qu'il le lit. La suite se recalcule à la fermeture.
  if (isOpen) return;
  queue = buildQueue(r.badges, seen);
  paintDot();
  if (queue.length && isMeVisible() && !snoozed) openAt(0);
}

let inflight: Promise<void> | null = null;
let again = false;

/** Redemande l'état au serveur. Une seule requête à la fois ; un appel pendant qu'elle court en
 *  relance une seule autre derrière, pour ne pas rater ce qui vient de changer. */
async function check(): Promise<void> {
  if (inflight) {
    again = true;
    return inflight;
  }
  inflight = (async () => {
    const token = await currentUser();
    if (!token) return;
    try {
      const fresh = await fetchBadges(token);
      // Le compte a changé pendant la requête : la réponse est celle de l'ancien.
      if ((await currentUser()) !== token) return;
      const owner = fingerprint(token);
      rememberBadges(fresh, owner);
      await consider(fresh, owner);
    } catch {
      /* hors ligne : la pastille attendra le prochain passage */
    }
  })().finally(() => {
    inflight = null;
    if (again) {
      again = false;
      void check();
    }
  });
  return inflight;
}

let debounce = 0;

/**
 * Une victoire vient d'arriver au serveur, ou un passage de synchronisation vient de finir
 * (src/sync.ts). Retardé et regroupé : au lancement, la synchronisation pousse les feuilles une à
 * une, et chacune appellerait ici — on attend que ça se calme, puis une seule requête.
 */
export function badgesMayHaveChanged(): void {
  clearTimeout(debounce);
  debounce = window.setTimeout(() => void check(), 1500);
}

// ---------- le dessin ----------

function tierName(tier: BadgeTier): string {
  return t(`progression.tier.${tier}` as I18nKey);
}

function plural(base: string, n: number): string {
  return t(`${base}.${n === 1 ? 'one' : 'other'}` as I18nKey, { n });
}

function paintQueue(): void {
  const n = queue.length;
  const dots = $('celQueue');
  if (n < 2) {
    dots.innerHTML = '';
    $('celCount').textContent = '';
    return;
  }
  dots.innerHTML = queue
    .map((_, i) => `<i class="${i < pos ? 'seen' : i === pos ? 'cur' : ''}"></i>`)
    .join('');
  $('celCount').textContent = `${pos + 1} / ${n}`;
}

/** « Bronze et Argent » dans la langue du joueur. Intl.ListFormat date de Chrome 72 : sur une
 *  WebView plus ancienne, son absence aurait levé une exception au milieu de show() et laissé un
 *  voile vide à l'écran — d'où le repli, et la construction seulement quand il y a à joindre. */
function joinTiers(names: string[]): string {
  try {
    return new Intl.ListFormat(currentLang(), { type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}

function medalCard(c: Extract<Card, { kind: 'medal' }>): string {
  const b = c.gain.badge;
  const tier = TIERS[rankOf(b)];
  const skipped = skippedTiers(c.gain);
  const skippedLine = skipped.length
    ? `<p class="cel-skipped cel-late">${skipped.map((k) => `<i class="cel-pip ${k}"></i>`).join('')}<span>${t(
        `celebrate.skipped.${skipped.length === 1 ? 'one' : 'other'}`,
        { tiers: joinTiers(skipped.map(tierName)) },
      )}</span></p>`
    : '';
  return `
    <div class="cel-eyebrow">${t('celebrate.eyebrow')}</div>
    <div class="cel-medal" id="celMedal">
      <canvas id="celCanvas" aria-hidden="true"></canvas>
      ${medal(b, 'xxl')}
      <span class="cel-ring" id="celRing"></span>
    </div>
    <h2 class="cel-name" id="celTitle">${badgeName(b)}</h2>
    <div class="cel-tier me-tier t-${tier}" id="celTier">${tierName(tier)}</div>
    ${skippedLine}
    <p class="cel-next cel-late">${progress(b).line}</p>`;
}

function mosaic(list: { badge: BadgeState }[]): string {
  return `<div class="cel-mosaic">${list
    .map(
      ({ badge }, i) => `
      <span class="cel-cell" style="--i:${i}">
        ${medal(badge, 'sm')}
        <span>${badgeName(badge)}<br><b class="me-tier t-${TIERS[rankOf(badge)]}">${tierName(TIERS[rankOf(badge)])}</b></span>
      </span>`,
    )
    .join('')}</div>`;
}

function moreCard(c: Extract<Card, { kind: 'more' }>): string {
  return `
    <div class="cel-eyebrow">${t('celebrate.more.eyebrow')}</div>
    <h2 class="cel-name" id="celTitle">${plural('celebrate.more.title', c.tiers)}</h2>
    ${mosaic(c.gains)}
    <p class="cel-next cel-late">${t('celebrate.more.line')}</p>`;
}

function welcomeCard(c: Extract<Card, { kind: 'welcome' }>): string {
  return `
    <div class="cel-eyebrow">${t('celebrate.welcome.eyebrow')}</div>
    <h2 class="cel-name" id="celTitle">${t('celebrate.welcome.title')}</h2>
    <div class="cel-total"><span id="celTotal">${c.tiers}</span><small>${t('celebrate.welcome.count')}</small></div>
    ${mosaic(c.badges.map((badge) => ({ badge })))}
    <p class="cel-next cel-late">${t('celebrate.welcome.line')}</p>`;
}

// ---------- l'animation ----------

let timers: number[] = [];
let raf = 0;

function stopAll(): void {
  for (const id of timers) clearTimeout(id);
  timers = [];
  cancelAnimationFrame(raf);
}

function later(ms: number, fn: () => void): void {
  timers.push(window.setTimeout(fn, ms));
}

interface Spot {
  x: number;
  y: number;
  r: number;
  c: number;
}

/** Les cases du disque, dans l'ordre où elles se peignent : une diagonale, par petites vagues
 *  désordonnées — comme une main qui colorie, pas comme une imprimante. */
function spots(): Spot[] {
  const cell = SIZE / GRID;
  const out: Spot[] = [];
  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      const x = (c + 0.5) * cell;
      const y = (r + 0.5) * cell;
      if (Math.hypot(x - SIZE / 2, y - SIZE / 2) <= SIZE / 2 - 1) out.push({ x, y, r, c });
    }
  }
  const key = new Map(out.map((p) => [p, p.r + p.c + (Math.random() - 0.5) * 2.2]));
  return out.sort((a, b) => key.get(a)! - key.get(b)!);
}

/** La teinte d'une case : sa place sous la lumière, venue d'en haut à gauche, choisit le ton du
 *  métal — un modelé en aplats, comme une feuille codée. Le Prisme prend le pigment de l'angle. */
function spotColor(p: Spot, tier: BadgeTier): string {
  const half = SIZE / 2;
  if (tier === 'prisme') {
    const a = (Math.atan2(p.y - half, p.x - half) + Math.PI) / (2 * Math.PI);
    return PIGMENTS[Math.floor(a * PIGMENTS.length) % PIGMENTS.length].css;
  }
  const m = METAL[tier];
  if (Math.hypot(p.x - half, p.y - half) / half > 0.86) return m[0];
  const d = Math.hypot(p.x - half * 0.55, p.y - half * 0.5) / half;
  return d < 0.45 ? m[3] : d < 0.95 ? m[2] : m[1];
}

/** Le creux de départ : la médaille pas encore peinte, en pointillé, comme sur l'écran « Moi ». */
function hollow(g: CanvasRenderingContext2D): void {
  const css = getComputedStyle(document.documentElement);
  const half = SIZE / 2;
  g.clearRect(0, 0, SIZE, SIZE);
  g.beginPath();
  g.arc(half, half, half - 2, 0, Math.PI * 2);
  g.fillStyle = css.getPropertyValue('--sink-2');
  g.fill();
  g.setLineDash([6, 6]);
  g.lineWidth = 2;
  g.strokeStyle = css.getPropertyValue('--line2');
  g.stroke();
  g.setLineDash([]);
}

/** Les points qui jaillissent : les neuf pigments, depuis le centre de la médaille. */
function burst(): void {
  const host = $('celMedal');
  for (let i = 0; i < 26; i++) {
    const dot = document.createElement('span');
    dot.className = 'cel-spark';
    dot.style.background = PIGMENTS[i % PIGMENTS.length].css;
    const size = 6 + Math.random() * 7;
    dot.style.width = dot.style.height = `${size}px`;
    host.appendChild(dot);
    const a = Math.random() * Math.PI * 2;
    const d = 70 + Math.random() * 70;
    dot
      .animate(
        [
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
          {
            transform: `translate(calc(-50% + ${Math.cos(a) * d}px), calc(-50% + ${Math.sin(a) * d + 40}px)) scale(.5)`,
            opacity: 0,
          },
        ],
        { duration: 900 + Math.random() * 300, easing: 'cubic-bezier(.2,.7,.3,1)' },
      )
      .finished.then(
        () => dot.remove(),
        () => dot.remove(),
      );
  }
}

function playMedal(tier: BadgeTier, first: boolean): void {
  const canvas = $<HTMLCanvasElement>('celCanvas');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = canvas.height = Math.round(SIZE * dpr);
  const g = canvas.getContext('2d')!;
  g.scale(dpr, dpr);
  hollow(g);
  const order = spots();
  const cell = SIZE / GRID;
  // Les cartes suivantes vont plus vite : le geste a déjà été vu une fois.
  const start = first ? 380 : 160;
  const dur = first ? 900 : 650;
  let painted = 0;
  const t0 = performance.now() + start;
  const step = (now: number): void => {
    const want = Math.min(order.length, Math.floor(((now - t0) / dur) * order.length));
    for (; painted < want; painted++) {
      const p = order[painted];
      g.fillStyle = spotColor(p, tier);
      g.beginPath();
      g.arc(p.x, p.y, cell / 2 + 0.2, 0, Math.PI * 2);
      g.fill();
    }
    if (painted < order.length) raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  // La feuille finie se fond en métal lisse : anneau de lumière, rebond, pigments.
  later(start + dur + 120, () => {
    $('celMedal').classList.add('lit');
    burst();
    buzz('medium');
  });
  later(start + dur + 320, () => $('celTier').classList.add('go'));
  later(start + dur + 520, () => $('celebrate').classList.add('done'));
}

function playList(first: boolean): void {
  const cells = document.querySelectorAll('#celBody .cel-cell').length;
  const total = document.getElementById('celTotal');
  if (total) {
    // Le compte monte jusqu'au total, en ralentissant : on voit qu'il y en a beaucoup.
    const n = Number(total.textContent);
    const t0 = performance.now();
    const up = (now: number): void => {
      const k = Math.min(1, (now - t0) / 900);
      total.textContent = String(Math.round(n * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(up);
    };
    total.textContent = '0';
    raf = requestAnimationFrame(up);
  }
  $('celBody').classList.add('go');
  later((first ? 220 : 120) + cells * 90 + 300, () => $('celebrate').classList.add('done'));
}

function show(i: number): void {
  stopAll();
  pos = i;
  const c = queue[i];
  const root = $('celebrate');
  const still = noMotion();
  root.classList.toggle('still', still);
  root.classList.remove('done');
  const body = $('celBody');
  body.classList.remove('go');
  body.innerHTML =
    c.kind === 'medal' ? medalCard(c) : c.kind === 'more' ? moreCard(c) : welcomeCard(c);
  paintQueue();
  const last = i === queue.length - 1;
  $('celLater').textContent = t(last ? 'common.close' : 'celebrate.later');
  $('celGo').textContent = t(last ? 'celebrate.see' : 'celebrate.next');
  if (still) {
    // Sans animation, la carte arrive dans son état final : la médaille dans son métal, tout
    // le texte en place. Rien de ce qu'elle dit ne dépend du mouvement.
    $('celMedal')?.classList.add('lit');
    $('celTier')?.classList.add('go');
    root.classList.add('done');
    return;
  }
  if (c.kind === 'medal') playMedal(TIERS[rankOf(c.gain.badge)], i === 0);
  else playList(i === 0);
}

function openAt(i: number): void {
  isOpen = true;
  focusBack = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  $('progressionDot').hidden = true;
  // Une fermeture pendant le glissement d'une carte à l'autre (les 180 ms de next()) laissait
  // `.out` posé : la carte suivante s'ouvrait alors invisible, boutons actifs, sous un voile vide.
  $('celPanel').classList.remove('out', 'pre');
  $('celebrate').setAttribute('aria-hidden', 'false');
  show(i);
  $('celGo').focus({ preventScroll: true });
}

/** Referme et recalcule ce qui reste : la pastille ne reste allumée que pour ce qui n'a pas été
 *  vu. */
function closeAll(): void {
  stopAll();
  isOpen = false;
  $('celPanel').classList.remove('out', 'pre');
  $('celebrate').setAttribute('aria-hidden', 'true');
  focusBack?.focus({ preventScroll: true });
  focusBack = null;
  queue = report && seen ? buildQueue(report.badges, seen) : [];
  paintDot();
}

function see(card: Card): void {
  if (!seen) return;
  seen = markSeen(seen, card);
  writeSeen(seen);
}

/** « Suivant » : la carte vue file à gauche, la suivante arrive de droite et rejoue son geste. */
function next(): void {
  see(queue[pos]);
  const panel = $('celPanel');
  if (noMotion()) {
    show(pos + 1);
    return;
  }
  stopAll();
  panel.classList.add('out');
  later(180, () => {
    panel.classList.remove('out');
    panel.classList.add('pre');
    void panel.offsetWidth;
    panel.classList.remove('pre');
    show(pos + 1);
  });
}

/** « Plus tard » (ou le fond, ou le bouton retour) : la carte à l'écran est vue, les suivantes
 *  attendent derrière la pastille, et le trophée les reprendra. */
function postpone(): void {
  see(queue[pos]);
  snoozed = true;
  closeAll();
}

/** « Voir mes badges » : tout ce que la file portait est vu, et l'écran « Moi », déjà dessous,
 *  amène le premier badge célébré sous les yeux. */
function seeAll(): void {
  const first = queue.find((c) => c.kind === 'medal');
  for (const c of queue) see(c);
  closeAll();
  if (first?.kind !== 'medal') return;
  const el = document.querySelector<HTMLElement>(
    `#progressionBody .me-badge[data-badge="${first.gain.badge.id}"]`,
  );
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: noMotion() ? 'auto' : 'smooth' });
  el.classList.add('cel-spot');
  window.setTimeout(() => el.classList.remove('cel-spot'), 1600);
}

/** Le trophée vient de s'ouvrir sur l'écran « Moi » : ce qui attend s'affiche tout de suite, sans
 *  attendre la réponse fraîche — celle-ci complétera la file à la fermeture si besoin. */
export function celebratePending(): void {
  snoozed = false;
  if (!isOpen && queue.length) openAt(0);
}

/** Même contrat que closeBadgeSheet() : vrai seulement si le geste a refermé quelque chose — ce
 *  qu'attend la chaîne du bouton retour d'Android (main.ts). Retour vaut « Plus tard ». */
export function closeCelebration(): boolean {
  if (!isOpen) return false;
  postpone();
  return true;
}

export function wireCelebrate(): void {
  // Les boutons et le fond n'agissent qu'une fois la carte posée (`.done`). Avant, ils étaient
  // invisibles mais actifs : un double tap sur « Suivant » sautait la carte suivante, comptée vue
  // sans avoir été vue, et un tap sur la liste « Moi » au moment où la carte s'ouvrait d'office
  // tombait sur le fond et la repoussait. Le CSS coupe aussi les clics (.cel:not(.done)).
  const ready = (): boolean => $('celebrate').classList.contains('done');
  $('celGo').onclick = () => {
    if (ready()) pos < queue.length - 1 ? next() : seeAll();
  };
  $('celLater').onclick = () => {
    if (ready()) postpone();
  };
  $('celebrate').onclick = (e) => {
    if (e.target === $('celebrate') && ready()) postpone();
  };
  onFreshBadges((r, owner) => void consider(r, owner));
  // La pastille dès le lancement, sans réseau : ce qui attendait la dernière fois attend encore —
  // à condition que le cache soit celui du compte actuel.
  void currentUser().then((token) => {
    if (!token) return;
    const owner = fingerprint(token);
    const cached = cachedBadges(owner);
    if (cached) void consider(cached, owner);
  });
}
